import { useMemo } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { WrappedInput } from '@quick/components/Forms';

import { useAppTranslation } from '@/app/i18n/client';
import { declaredValueLimits } from '@/layers/core/data/country';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useCurrencyUtils } from '@/layers/core/deal/services/useCurrencyUtils';
import { useUserInfo } from '@/layers/core/deal/services/useUserInfo';
import { idToCountry } from '@/layers/core/domain/countryMap';

import { useFormDataContext } from './FormDataContext';
import type { IForm } from './types';

export function DeclaredValue() {
  const { t } = useAppTranslation(['common', 'demand']);
  const {
    control,
    formState: { errors },
  } = useFormContext<IForm>();
  const { formatValue, cleanValue } = useCurrencyUtils();
  const { modality } = useFormDataContext();

  const requiresDeclaredValue = modality?.declared_value;

  const [minDeclaredValue, maxDeclaredValue] = useGetMinAndMaxDeclaredValue();

  const alertMessage = t('demand:declaredValue.alertCheck', {
    min: minDeclaredValue,
    max: maxDeclaredValue,
  });

  if (!requiresDeclaredValue) return null;
  return (
    <Controller
      control={control}
      name="declared_value"
      rules={{
        shouldUnregister: true,
        validate: (value) => {
          if (!value && requiresDeclaredValue)
            return t('common:msg.required_field');
          if (!!value && value < minDeclaredValue) return alertMessage;
          if (!!value && value > maxDeclaredValue) return alertMessage;
        },
      }}
      render={({ field }) => (
        <WrappedInput
          {...field}
          isOnModal
          id="declaredValue"
          label={t('demand:declaredValue.header')}
          placeholder={t('demand:declaredValue.placeholder')}
          className=""
          inputMode="decimal"
          autoComplete="off"
          innerClassName=""
          value={field.value ? formatValue(field.value) : ''}
          onChange={(e) => {
            const value = Number.parseFloat(cleanValue(e.target.value));
            field.onChange(Number.isNaN(value) ? undefined : value);
          }}
          symbol={t('demand:declaredValue.sign')}
          error={errors.declared_value?.message}
        />
      )}
    />
  );
}

function useGetMinAndMaxDeclaredValue(): [number, number] {
  const { data: meData } = useUserInfo();
  const { user } = useFormDataContext();
  const maxDeclaredForUser =
    typeof user?.max_declared_value === 'number'
      ? user.max_declared_value.toString()
      : user?.max_declared_value;

  const { country } = useCountry();
  const userCountry = idToCountry?.[(user?.country_id as 1) ?? (0 as 1)];
  const { watch } = useFormContext<IForm>();
  const lineId = watch('line_id');
  const userMaxDeclaredValue = Number.parseFloat(
    maxDeclaredForUser ?? meData?.max_declared_value ?? ''
  );

  return useMemo(
    () => [
      declaredValueLimits[userCountry ?? country][lineId as 1]?.min ??
        declaredValueLimits[userCountry ?? country][3].min,
      Number.isNaN(userMaxDeclaredValue)
        ? declaredValueLimits[userCountry ?? country][lineId as 1]?.max ??
          declaredValueLimits[userCountry ?? country][3].max
        : userMaxDeclaredValue,
    ],
    [country, lineId, userCountry, userMaxDeclaredValue]
  );
}
